/**
 * Recurrence types for reminders
 */
export enum RecurrenceType {
  Daily = 0,
  Weekly = 1,
  Monthly = 2,
  Yearly = 3,
  Custom = 4,
}

/**
 * Days of week bitmask flags
 */
export enum DaysOfWeek {
  None = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 4,
  Thursday = 8,
  Friday = 16,
  Saturday = 32,
  Sunday = 64,
  Weekdays = Monday | Tuesday | Wednesday | Thursday | Friday,
  Weekends = Saturday | Sunday,
  All = Monday | Tuesday | Wednesday | Thursday | Friday | Saturday | Sunday,
}

/**
 * Recurrence pattern for reminders
 */
export interface RecurrencePattern {
  recurrenceType: RecurrenceType | null
  recurrenceInterval: number | null
  recurrenceDaysOfWeek: number | null
  recurrenceDayOfMonth: number | null
  recurrenceEndDate: string | null
  recurrenceMaxOccurrences: number | null
}

/**
 * Helper to get day name from DaysOfWeek enum
 */
export function getDayName(day: DaysOfWeek): string {
  switch (day) {
    case DaysOfWeek.Monday:
      return 'Monday'
    case DaysOfWeek.Tuesday:
      return 'Tuesday'
    case DaysOfWeek.Wednesday:
      return 'Wednesday'
    case DaysOfWeek.Thursday:
      return 'Thursday'
    case DaysOfWeek.Friday:
      return 'Friday'
    case DaysOfWeek.Saturday:
      return 'Saturday'
    case DaysOfWeek.Sunday:
      return 'Sunday'
    default:
      return ''
  }
}

/**
 * Helper to get short day name from DaysOfWeek enum
 */
export function getShortDayName(day: DaysOfWeek): string {
  switch (day) {
    case DaysOfWeek.Monday:
      return 'Mon'
    case DaysOfWeek.Tuesday:
      return 'Tue'
    case DaysOfWeek.Wednesday:
      return 'Wed'
    case DaysOfWeek.Thursday:
      return 'Thu'
    case DaysOfWeek.Friday:
      return 'Fri'
    case DaysOfWeek.Saturday:
      return 'Sat'
    case DaysOfWeek.Sunday:
      return 'Sun'
    default:
      return ''
  }
}

/**
 * Get human-readable recurrence description
 */
export function getRecurrenceDescription(
  pattern: RecurrencePattern,
  locale?: string,
): string {
  if (
    pattern.recurrenceType === null ||
    pattern.recurrenceType === undefined
  ) {
    return 'Does not repeat'
  }

  const interval = pattern.recurrenceInterval ?? 1

  let description = ''

  switch (pattern.recurrenceType) {
    case RecurrenceType.Daily:
      description = interval === 1 ? 'Daily' : `Every ${interval} days`
      break

    case RecurrenceType.Weekly: {
      const days: Array<string> = []
      if (pattern.recurrenceDaysOfWeek) {
        if (pattern.recurrenceDaysOfWeek & DaysOfWeek.Monday)
          days.push('Mon')
        if (pattern.recurrenceDaysOfWeek & DaysOfWeek.Tuesday)
          days.push('Tue')
        if (pattern.recurrenceDaysOfWeek & DaysOfWeek.Wednesday)
          days.push('Wed')
        if (pattern.recurrenceDaysOfWeek & DaysOfWeek.Thursday)
          days.push('Thu')
        if (pattern.recurrenceDaysOfWeek & DaysOfWeek.Friday)
          days.push('Fri')
        if (pattern.recurrenceDaysOfWeek & DaysOfWeek.Saturday)
          days.push('Sat')
        if (pattern.recurrenceDaysOfWeek & DaysOfWeek.Sunday)
          days.push('Sun')
      }

      const daysStr = days.join(', ')
      description =
        interval === 1
          ? `Weekly on ${daysStr}`
          : `Every ${interval} weeks on ${daysStr}`
      break
    }

    case RecurrenceType.Monthly:
      description =
        interval === 1
          ? `Monthly on day ${pattern.recurrenceDayOfMonth}`
          : `Every ${interval} months on day ${pattern.recurrenceDayOfMonth}`
      break

    case RecurrenceType.Yearly:
      description = interval === 1 ? 'Yearly' : `Every ${interval} years`
      break

    case RecurrenceType.Custom:
      description = 'Custom recurrence'
      break
  }

  // Add end condition
  if (pattern.recurrenceEndDate) {
    description += ` until ${new Date(pattern.recurrenceEndDate).toLocaleDateString(locale)}`
  } else if (pattern.recurrenceMaxOccurrences) {
    description += `, ${pattern.recurrenceMaxOccurrences} times`
  }

  return description
}
